// src/services/documentService.ts
// Usa apiClient già configurato con baseURL + auth headers
import apiClient from './apiClient';
import { DocumentItem, DocDiff } from '../types/storage';
import { getActiveDocumentPrefix } from '../contexts/ConfigContext';

export type ScrapingStatus = {
  running: boolean;
  done: number;
  total: number;
  phase: 'idle' | 'discovery' | 'scraping' | 'finalizing' | 'approving' | 'done';
  ok: boolean | null;
};

export type SuppressedItem = DocumentItem & {
  is_suppressed?: boolean;
  suppressed_reason?: string | null;
  suppressed_at?: string | null;
};

class DocumentService {
  // Elenco ATTIVI (proxy /api/gcs/documents nasconde già i soppressi)
  listDocuments = async (prefix = getActiveDocumentPrefix()): Promise<DocumentItem[]> => {
    const res = await apiClient.get('/api/gcs/documents', { params: { prefix } });
    // tool proxy restituisce { status, data: [...] }
    return res.data?.data ?? [];
  };

  // 🔹 Elenco MODIFICATI (status=manual) – scheda dedicata
  listModifiedDocuments = async (): Promise<DocumentItem[]> => {
    const res = await apiClient.get('/api/gcs/documents/modified');
    // a seconda del tool può tornare {items:[...]} o {data:[...]} – gestiamo entrambi
    return res.data?.items ?? res.data?.data ?? [];
  };

  // Elenco RIMOSSI/SOPPRESSI
  listRemovedDocuments = async (): Promise<SuppressedItem[]> => {
    const res = await apiClient.get('/api/gcs/documents/suppressed');
    // endpoint proxy ritorna { items: [...] }
    return res.data?.items ?? [];
  };

  // Testo ricostruito (fallback content/text)
  getDocumentText = async (docId: string): Promise<string> => {
    const res = await apiClient.get(`/api/gcs/documents/${docId}/text`);
    return res.data?.content ?? res.data?.text ?? '';
  };

  getDocumentDiff = async (docId: string): Promise<DocDiff> => {
    const res = await apiClient.get(`/api/gcs/documents/${docId}/diff`);
    return res.data?.diff ?? {};
  };

  // APPROVA / RIFIUTA pending
  approveDocument = (docId: string) =>
    apiClient.post(`/api/gcs/documents/${docId}/approve`);

  rejectDocument = (docId: string) =>
    apiClient.post(`/api/gcs/documents/${docId}/reject`);

  // SOPPRIMI (rimuovi dalla vista “Attivi” + escludi dal retrieval)
  suppressDocument = (docId: string, reason: string) =>
    apiClient.post(`/api/gcs/documents/${docId}/suppress`, { reason });

  // RIPRISTINA (torna indexed)
  unsuppressDocument = (docId: string) =>
    apiClient.delete(`/api/gcs/documents/${docId}/suppress`);

  // (Opzionale) DELETE hard
  deleteDocument = (docId: string) =>
    apiClient.delete(`/api/gcs/documents/${docId}`);

  // Avvio scraping manuale (Make.com → backend locale)
  triggerScraping = () =>
    apiClient.post('/api/trigger-scraping');

  // Avanzamento reale dello scraping in corso
  getScrapingStatus = async (): Promise<ScrapingStatus> => {
    const res = await apiClient.get('/api/scraping-status');
    return res.data;
  };

  // 🔹 Salvataggio manuale (blocca scraping, status=manual, rigenera chunk/embeddings lato tool)
  manualSave = (docId: string, text: string, editedBy?: string) =>
    apiClient.post(`/api/gcs/documents/${docId}/manual-edit`, {
      text,
      updated_by: editedBy, // uso updated_by per coerenza col backend
    });
}

export const documentService = new DocumentService();
